import ControlViewModel from '@/ViewModels/ControlViewModel';
import ToolBarViewModel from '@/ViewModels/Controls/ToolBarViewModel';
import * as DefaultViewExtensionInterfaces from '#/DefaultViewExtensionInterfaces';
import * as CoreFrameInterfaces from '#/CoreFrameInterfaces';
import MediumUploadHelper from '@/Utility/MediumUploadHelper';
import RADApplication from '@/Management/RADApplication';
import ComponentManager from '@/Management/ComponentManager';
import { IResultDTO } from "coreframetypings/dto";
import DataGridViewModel from '@/ViewModels/Controls/DataGridViewModel';

/**
 * Das ViewModel für die AssociationGrid
 */
export default class AssociationGridViewModel extends ControlViewModel {

    /**
     * Das ViewModel für die ToolBar
     */
    public toolBar: ToolBarViewModel;

    /**
     * Das ViewModel für die Tabelle
     */
    public dataGrid: DataGridViewModel;

    /**
     * Der Name der Association bei einem AssociationDataGrid, ansonsten NULL
     */
    public associationName: string;

    /**
     * Der Id des Objektes bei einem AssociationDataGrid, ansonsten NULL
     */
    public associationPdoId: number;

    /**
     * Der Typ des Objektes bei einem AssociationDataGrid, ansonsten NULL
     */
    public associationType: string;

    /**
     * Der Typ des Objektes bei einem AssociationDataGrid
     */
    public contextTypeName: string;

    /**
     * Die Id des Objektes bei einem AssociationDataGrid
     */
    public contextPdoId: string;

    /**
     * Der Typ der zu verbindenen Objekte bei einem AssociationDataGrid
     */
    public associationTypeName: string;

    /**
     * Die Assoziationen zu denen nicht navigiert werden darf
     */
    public notNavigateableAssociations: string[];

    /**
     * Gibt an ob die Drop-Funktion für das Control aktiv ist.
     */
    public isDropable: boolean;

    /*
     * Wird ausgeführt wenn sich die Assoziation ändert.
     */
    public onAssociationChanged: Function;

    /**
     * @param {object} params - Ein Objekt mit Optionen für das Control
     */
    constructor(params: DefaultViewExtensionInterfaces.IAssociationGridViewModelConstructorParameter) {
        super(params);
        this.toolBar = this.paramOrDefault(params.toolBar, null);
        this.dataGrid = this.paramOrDefault(params.dataGrid, null);
        this.contextTypeName = this.paramOrDefault(params.contextTypeName, null);
        this.contextPdoId = params.contextPdoId;
        this.associationTypeName = params.associationTypeName;
        this.associationName = params.associationName;
        this.isDropable = this.paramOrDefault(params.isDropable, false);
        this.onAssociationChanged = this.handlerOrDefault(params.onAssociationChanged);
        this.notNavigateableAssociations = [];
    }

    /*
     * Wird nach dem DropEvent ausgeführt.
     */
    public onDrop(draggedElements: CoreFrameInterfaces.DroppedElements): void {
        if (!this.isEnabled())
            return;

        if (draggedElements.links.length > 1)
            return;

        const isLink = draggedElements.links.length === 1;

        let isOneSuccessfull = false;
        const count = isLink ? 1 : draggedElements.files.length;
        const observableCounter = this.observableOrDefault(count);
        this.defineSubscription(observableCounter, (counter) => {
            if (counter === 0 && isOneSuccessfull) {
                this.dataGrid.refresh();
                this.onAssociationChanged();
            }
        });
        const isShowSavingOn = RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowSavingDialogOnStackNavigation');
        const handleResult = (resultDTO) => {
            if (this.validateStatus(resultDTO)) {
                isOneSuccessfull = true;
                // Wenn "ShowSavingDialogOnStackNavigation=false" ist, darf zu den neu angelegten Assoziationen nicht navigiert werden.
                if (!isShowSavingOn)
                    this.notNavigateableAssociations.push(resultDTO.returnValue);
            }
            observableCounter(observableCounter() - 1);
        };

        this.increaseBusyCounter();
        if (isLink) {
            _.each(draggedElements.links,
                (link) => {
                    MediumUploadHelper.handleGridContent(this.contextPdoId,
                        this.contextTypeName,
                        this.associationTypeName,
                        this.associationName,
                        false,
                        link)
                        .always((resultDTO: IResultDTO) => {
                            handleResult(resultDTO);
                            this.decreaseBusyCounter();
                        });
                });
        } else {
            const result = MediumUploadHelper.handleGridFiles(this.contextPdoId,
                this.contextTypeName,
                this.associationTypeName,
                this.associationName,
                draggedElements.files);
            for (const fileName in result) {
                const promise = result[fileName];
                promise.always((resultDTO: IResultDTO) => { handleResult(resultDTO); })
            }
            Promise.all(Object.values(result)).finally(() => {
                this.decreaseBusyCounter();
            });
        }
    }

    /**
     * Prüft ob die gedraggten Elemente zulässig sind
     * @param draggedElements
     */
    public checkDraggedElementsAllowed(draggedElements: CoreFrameInterfaces.DraggedElements): boolean {
        // Wir können hier leider nichts ausschließen, da selbst text/plain ein Link sein könnte
        return draggedElements.isFile || draggedElements.isText;
    }

}

ComponentManager.setViewModel('AssociationGridViewModel', AssociationGridViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');